import React, { forwardRef, MouseEventHandler, Ref, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { ApiBridgeAlert, ApiSanctionAlert } from '../../api';
import { DropdownWrapper } from '../DropdownWrapper';
import Has from '../../auth/Has';
import { Avatar, Button } from '@salvapp/ui-components';
import { alertAssignmentInternalApi } from '../../apis';
import {
  InternalApiAlertType,
  InternalApiUserBasicDetails,
  InternalApiManualAlert,
  InternalApiMonitoringAlert,
} from '../../api-internal';
import { useMe } from '../../organisation/useMe';
import { useAssignableUsers } from '../../users/useAssignableUsers';

interface AlertAssignmentProps {
  alert: ApiSanctionAlert | ApiBridgeAlert | InternalApiManualAlert | InternalApiMonitoringAlert;
  defaultSize?: number;
  afterChange: () => void;
  permissions?: string[];
  alertType: InternalApiAlertType;
}

export const AlertAssignment = ({
  alert,
  defaultSize = 40,
  afterChange,
  permissions,
  alertType,
}: AlertAssignmentProps): React.JSX.Element => {
  const currentUser = useMe();
  const { assignableUsers } = useAssignableUsers();

  interface ToggleProps {
    onClick: MouseEventHandler;
  }

  const [displayedAssignments, setDisplayedAssignments] = useState(assignableUsers?.slice(0, defaultSize));

  useEffect(() => {
    setDisplayedAssignments(assignableUsers?.slice(0, defaultSize));
  }, [assignableUsers, defaultSize]);

  const Toggle = forwardRef(({ onClick }: ToggleProps, ref: Ref<any>) => {
    const handleClick = (e: any) => {
      e.preventDefault();
      onClick(e);
    };

    return (
      <Has anyPermissions={permissions}>
        {(can: boolean) =>
          can ? (
            (alert.assignee && (
              <Button
                variant='white'
                className={`d-flex flex-row align-items-center dropdown-toggle pt-2 pb-2`}
                ref={ref}
                onClick={handleClick}
              >
                <Avatar email={alert.assignee.email} />
                <span className='ms-2'>{alert.assignee.name}</span>
              </Button>
            )) || (
              <Button
                variant='white'
                ref={ref}
                onClick={handleClick}
                className={`btn-white d-flex align-items-center dropdown dropdown-toggle pt-2 pb-2`}
              >
                Assign
              </Button>
            )
          ) : (
            <div>
              {alert.assignee && (
                <div>
                  <Avatar email={alert.assignee.email} />
                  <span className='ms-2'>{alert.assignee.name}</span>
                </div>
              )}
            </div>
          )
        }
      </Has>
    );
  });

  Toggle.displayName = 'Toggle';

  const handleClick = async (user: InternalApiUserBasicDetails) => {
    if (!user.id) {
      return;
    }
    await alertAssignmentInternalApi.assignAlert({
      internalApiAlertAssignmentCommand: {
        alertId: alert.id,
        assigneeId: user.id,
        alertType,
      },
    });
    afterChange();
  };

  const handleAssignToMe = async () => {
    if (!currentUser.isLoading) await handleClick(currentUser);
  };

  const searchUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setDisplayedAssignments(
      assignableUsers?.filter((user) => {
        return (
          user.email && (user.name.toLocaleLowerCase().includes(query) || user.email.toLowerCase().includes(query))
        );
      })
    );
  };

  return (
    <DropdownWrapper className='assignee'>
      <Dropdown.Toggle as={Toggle as any} id='chooseAssignee' />
      <Dropdown.Menu align={{ sm: 'start' }}>
        <div className='row px-4'>
          <div className='col dropdown-search-assignee'>
            <input
              type='text'
              className='form-control form-control-sm'
              placeholder='Search ....'
              onChange={(e) => searchUsers(e)}
              autoFocus
            />
          </div>
          {currentUser && (
            <Dropdown.Item className='col assign-self-button'>
              <Button size='medium' variant='outline-primary' onClick={handleAssignToMe}>
                Assign to me
              </Button>
            </Dropdown.Item>
          )}
        </div>
        <Dropdown.Divider />
        <div className='items'>
          {displayedAssignments?.map((user) => (
            <Dropdown.Item
              key={user.id}
              as='button'
              className='d-flex flex-row align-items-center dropdown-item-regular'
              onClick={() => handleClick(user)}
            >
              <Avatar email={user.email} />
              <div className='d-flex flex-column'>
                <div className='ms-2'>{user.name}</div>
                <div className='text-muted ms-2 small'>{user.email}</div>
              </div>
            </Dropdown.Item>
          ))}
        </div>
      </Dropdown.Menu>
    </DropdownWrapper>
  );
};

export default AlertAssignment;
